// Silo Art V2 ("Elevated Mode") — shared helpers.
//
// V2 weaves each cube face's EXISTING image into the real page it represents,
// as a soft background layer. No new images are generated — this only stores
// the LOOK choices, so it's a pure client-side theming layer (zero think-engine
// load). Images come from the current "leader" silo's cube faces.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const ART_MODES = ["off", "v1", "v2"];

export const FILTERS = [
  { id: "none", label: "Pure" },
  { id: "pixelate", label: "Pixelate" },
  { id: "grayscale", label: "Mono" },
  { id: "blur", label: "Blur" },
  { id: "vivid", label: "Vivid" },
];

// Jul 25, 2026 (Randy) — PLATFORM STAMP: these defaults were locked to Randy's
// eyeballed V2 settings. Any "default"/reset now resolves to these values.
//
// Aug 2026 (Randy pt-default-sweep) — Second stamp after Randy drifted on
// four mobile/text toggles between Jul 25 and Aug 7. Current preview state
// re-locked as the new platform baseline. Precedence rule for future
// changes: an existing user's saved config still wins (b — the sane rule);
// only users who never explicitly toggled these fields inherit the update.
export const DEFAULT_V2 = {
  leader_mode: "dynamic",
  leader_pin_silo_id: null,
  bold_unboxed: false,
  bold_unboxed_mobile: true,          // Aug 2026 stamp: was false → Randy uses it on mobile
  black_text: false,
  black_text_mobile: false,           // Aug 2026 stamp: was true → Randy switched to white
  // Feb 2026 (Randy pt-siloart-white-text) — mirror pair to the
  // black_text toggles. Mutually exclusive with black_text at the
  // same scope (setter in SiloArtStage2Panel enforces this). Both
  // ONLY fire when V2 theming is on — the CSS selectors are all
  // scoped under body.siloart-* which itself is gated by
  // body.siloart-v2 (see SiloArtLayer.jsx).
  //
  // Aug 2026 stamp: both flipped from false → true. Randy's whole-site
  // text is white by default now (desktop and mobile).
  white_text: true,
  white_text_mobile: true,
  dark_ignore_canvas: true,
  pages: { assistant: true, public_qa: true },
};

// Per-silo LOOK defaults (stored on each silo document). Content canvas + band.
export const DEFAULT_ZONES = {
  page: { source: "image", filter: "none", dim: 15, color: "#FDFCF8", face: null },
  band: { source: "image", filter: "none", dim: 16, color: "#bda289", face: "users" },
};

const _FILTER_IDS = new Set(FILTERS.map((f) => f.id));

// Normalize a silo's stored look (silo.silo_art) → {zones:{page,band}}.
// The band may be OFF; the content canvas is always image/color.
export function normalizeZones(raw) {
  const zin = (raw && raw.zones) || raw || {};
  const merge = (def, z, allowOff) => {
    z = z && typeof z === "object" ? z : {};
    const allowed = allowOff ? ["image", "color", "off"] : ["image", "color"];
    return {
      source: allowed.includes(z.source) ? z.source : def.source,
      filter: _FILTER_IDS.has(z.filter) ? z.filter : "none",
      dim: Math.max(0, Math.min(85, Number.isFinite(+z.dim) ? +z.dim : def.dim)),
      color: (typeof z.color === "string" && z.color.startsWith("#")) ? z.color : def.color,
      face: (typeof z.face === "string" && z.face) ? z.face : (def.face || null),
    };
  };
  // Platform stamp: feature-page dim defaults to 5 when unset.
  const clampDimOrNull = (v) => {
    if (v === null || v === undefined || v === "") return 5;
    const n = +v;
    return Number.isFinite(n) ? Math.max(0, Math.min(85, n)) : 5;
  };
  const pd = (zin.page_dim && typeof zin.page_dim === "object") ? zin.page_dim : {};
  // Jul 2026 (Randy) — per-page canvas effects for the two feature pages:
  // art zoom (phones only), hero text scale, forced text colour, hide text.
  const fxNum = (v, lo, hi, dflt) => {
    if (v === null || v === undefined || v === "") return dflt;
    const n = +v;
    return Number.isFinite(n) ? Math.max(lo, Math.min(hi, Math.round(n))) : dflt;
  };
  const fxOne = (o) => {
    o = (o && typeof o === "object") ? o : {};
    return {
      zoom: fxNum(o.zoom, 40, 250, 100),
      text_scale: fxNum(o.text_scale, 10, 140, 50),
      text_color: (o.text_color === "black" || o.text_color === "white") ? o.text_color : "white",
      text_hidden: o.text_hidden === true,
      // Feb 17, 2026 (Randy pt-feature-page-model-b) — per-page kill switch.
      // When true, silo art on this specific feature page is FORCED OFF
      // regardless of what the creator-dashboard slider says. Default false
      // → follows the dashboard master (V1 or V2 = art shows).
      keep_art_off: o.keep_art_off === true,
    };
  };
  const pf = (zin.page_fx && typeof zin.page_fx === "object") ? zin.page_fx : {};
  return {
    page: merge(DEFAULT_ZONES.page, zin.page, true),
    band: merge(DEFAULT_ZONES.band, zin.band, true),
    page_dim: {
      assistant: clampDimOrNull(pd.assistant),
      public_qa: clampDimOrNull(pd.public_qa),
    },
    page_fx: {
      assistant: fxOne(pf.assistant),
      public_qa: fxOne(pf.public_qa),
    },
  };
}

// Merge a (possibly partial / null) server GLOBAL config over the defaults.
// The zone LOOK is per-silo now (see normalizeZones) and lives on the silo.
export function normalizeV2(cfg) {
  const base = JSON.parse(JSON.stringify(DEFAULT_V2));
  if (!cfg || typeof cfg !== "object") return base;
  base.leader_mode = cfg.leader_mode === "pinned" ? "pinned" : "dynamic";
  base.leader_pin_silo_id = typeof cfg.leader_pin_silo_id === "string" ? cfg.leader_pin_silo_id : null;
  if (typeof cfg.bold_unboxed === "boolean") base.bold_unboxed = cfg.bold_unboxed;
  if (typeof cfg.bold_unboxed_mobile === "boolean") base.bold_unboxed_mobile = cfg.bold_unboxed_mobile;
  if (typeof cfg.black_text === "boolean") base.black_text = cfg.black_text;
  if (typeof cfg.black_text_mobile === "boolean") base.black_text_mobile = cfg.black_text_mobile;
  // Feb 2026 (Randy pt-siloart-white-text) — sibling to black_text.
  if (typeof cfg.white_text === "boolean") base.white_text = cfg.white_text;
  if (typeof cfg.white_text_mobile === "boolean") base.white_text_mobile = cfg.white_text_mobile;
  if (typeof cfg.dark_ignore_canvas === "boolean") base.dark_ignore_canvas = cfg.dark_ignore_canvas;
  const p = cfg.pages || {};
  if (typeof p.assistant === "boolean") base.pages.assistant = p.assistant;
  if (typeof p.public_qa === "boolean") base.pages.public_qa = p.public_qa;
  return base;
}

// Cube face each CHROMELESS creator-dashboard tab borrows from the leader silo.
// (Innate feature pages use their own face — see faceForPath.)
export const CHROME_FACE_BY_TAB = {
  silos: "wars",
  users: "staging",
  earnings: "workbench",
  profile: "file-refinery",
  interests: "conversations",
  ops: "users",
  comms: "operations",
  faq: "probe",
};

// Route → cube face. Innate feature pages map to their own face; chromeless
// pages borrow (see mapping). Public/auth surfaces are intentionally excluded
// upstream (no leader for anonymous visitors).
export function faceForPath(pathname = "") {
  if (pathname === "/" || pathname.startsWith("/m/")) return "account"; // marketing
  if (pathname.startsWith("/silo/") && pathname.includes("/assistant")) return "assistant";
  if (pathname.startsWith("/s/")) return "public-qa";
  if (pathname.startsWith("/wars")) return "wars";
  if (pathname.startsWith("/dashboard/account-settings")) return "file-refinery"; // profile
  if (pathname.startsWith("/dashboard/silo/")) return "center"; // silo manage → Home/main
  if (pathname.startsWith("/dashboard")) return "wars"; // Your Silos default
  if (pathname.startsWith("/fan-portal")) return "fan-portal";
  if (pathname.startsWith("/admin")) return "admin";
  return "center";
}

// Which surfaces get the V2 background (creator chrome + owner-viewed feature
// pages). Anonymous visitors have no leader, so these no-op for them.
export function pathIsThemed(pathname = "") {
  return (
    pathname === "/" ||
    pathname.startsWith("/m/") ||
    pathname.startsWith("/dashboard") ||
    pathname.startsWith("/fan-portal") ||
    pathname.startsWith("/silo/") ||
    pathname.startsWith("/s/") ||
    pathname.startsWith("/wars") ||
    pathname.startsWith("/admin")
  );
}

// Feature pages that carry their own per-page V2 opt-out toggle (Stage 2).
export function optOutKeyForPath(pathname = "") {
  if (pathname.startsWith("/silo/") && pathname.includes("/assistant")) return "assistant";
  if (pathname.startsWith("/s/")) return "public_qa";
  return null;
}

// Resolve a cube face image URL for a silo, falling back to its center face.
export function faceImageUrl(silo, faceId) {
  const faces = silo?.cube?.faces || {};
  const imgId = faces[faceId]?.image_id || faces.center?.image_id;
  return imgId ? `${BACKEND_URL}/api/silo-images/${imgId}` : null;
}

export const FACE_LABELS = {
  center: "Home", assistant: "AI Assistant", "public-qa": "Public Q&A",
  account: "Account & Billing", admin: "Admin", "fan-portal": "Fan Portal",
  wars: "Silo Wars", staging: "Staging", workbench: "Workbench",
  "file-refinery": "Refinery", conversations: "Chats", ytre: "YTRE", users: "Users",
  operations: "Ops", probe: "Probe", tone: "Tone", settings: "Settings",
};
